/**
 * Unified Resize Manager for SmarterElements
 * Replaces the conflicting polling and ResizeObserver systems
 * with a single, efficient, and smooth resizing solution
 */

export interface ResizeOptions {
  minHeight?: number;
  maxHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  debounceMs?: number;
  elementId?: string;
}

export interface ResizeMessage {
  type: 'resize' | 'getHeight' | 'constraintsUpdate';
  height?: number;
  width?: number;
  elementId?: string;
  constraints?: ResizeOptions;
  [key: string]: unknown;
}

/**
 * Unified resize manager for iframe content (runs inside iframe)
 */
export class IframeContentResizeManager {
  private resizeObserver: ResizeObserver | null = null;
  private debounceTimeout: number | null = null;
  private lastHeight: number = 0;
  private lastWidth: number = 0;
  private animationFrameId: number | null = null;
  private isDestroyed: boolean = false;

  constructor(private options: ResizeOptions = {}) {
    this.options = {
      minHeight: 100,
      maxHeight: Infinity,
      minWidth: 0,
      maxWidth: Infinity,
      debounceMs: 16, // 60fps
      ...options,
    };

    this.initialize();
  }

  private initialize(): void {
    if (typeof window === 'undefined' || !window.parent || window.parent === window) {
      console.warn('IframeContentResizeManager should only be used inside an iframe');
      return;
    }

    this.setupResizeObserver();
    this.setupMessageListener();
    this.calculateAndSendDimensions();
  }

  private setupResizeObserver(): void {
    if (!window.ResizeObserver) {
      console.warn('ResizeObserver not supported, falling back to mutation observer');
      this.setupFallbackObserver();
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.debouncedDimensionUpdate();
    });

    // Observe document elements
    this.resizeObserver.observe(document.documentElement);
    this.resizeObserver.observe(document.body);

    // Observe elements with data-observe-resize attribute
    const observedElements = document.querySelectorAll('[data-observe-resize]');
    observedElements.forEach(el => {
      this.resizeObserver!.observe(el);
    });
  }

  private setupFallbackObserver(): void {
    // Fallback for browsers without ResizeObserver
    const mutationObserver = new MutationObserver(() => {
      this.debouncedDimensionUpdate();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Also listen for window resize
    window.addEventListener('resize', () => {
      this.debouncedDimensionUpdate();
    });
  }

  private setupMessageListener(): void {
    const messageHandler = (event: MessageEvent<ResizeMessage>) => {
      if (this.isDestroyed) return;

      try {
        const { data } = event;
        if (!data || typeof data !== 'object') return;

        // Handle getHeight requests
        if (data.type === 'getHeight') {
          if (!this.options.elementId || data.elementId === this.options.elementId) {
            this.calculateAndSendDimensions();
          }
        }

        // Handle constraint updates
        if (data.type === 'constraintsUpdate' && data.constraints) {
          if (!this.options.elementId || data.elementId === this.options.elementId) {
            this.updateConstraints(data.constraints);
          }
        }
      } catch (error) {
        console.error('Error handling resize message:', error);
      }
    };

    window.addEventListener('message', messageHandler);
  }

  private debouncedDimensionUpdate = (): void => {
    if (this.isDestroyed) return;

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = window.setTimeout(() => {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      this.animationFrameId = requestAnimationFrame(() => {
        this.calculateAndSendDimensions();
      });
    }, this.options.debounceMs);
  };

  private calculateAndSendDimensions(): void {
    if (this.isDestroyed) return;

    try {
      // Calculate height using multiple methods for accuracy
      const bodyHeight = document.body.offsetHeight;
      const htmlHeight = document.documentElement.offsetHeight;
      const bodyScrollHeight = document.body.scrollHeight;
      const htmlScrollHeight = document.documentElement.scrollHeight;

      // Get the maximum height
      let height = Math.max(bodyHeight, htmlHeight, bodyScrollHeight, htmlScrollHeight);

      // Apply constraints
      height = Math.max(height, this.options.minHeight || 100);
      height = Math.min(height, this.options.maxHeight || Infinity);

      // Calculate width (for future constraint support)
      const width = Math.max(
        document.body.offsetWidth,
        document.documentElement.offsetWidth
      );

      // Only send if dimensions changed significantly
      const heightChanged = Math.abs(height - this.lastHeight) > 1;
      const widthChanged = Math.abs(width - this.lastWidth) > 1;

      if (heightChanged || widthChanged) {
        this.lastHeight = height;
        this.lastWidth = width;

        const message: ResizeMessage = {
          type: 'resize',
          height,
          width,
        };

        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }

        window.parent.postMessage(message, '*');
      }
    } catch (error) {
      console.error('Error calculating dimensions:', error);
    }
  }

  public updateConstraints(constraints: ResizeOptions): void {
    this.options = { ...this.options, ...constraints };
    // Recalculate with new constraints
    this.calculateAndSendDimensions();
  }

  public destroy(): void {
    this.isDestroyed = true;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}

/**
 * Unified resize manager for parent window (runs in host application)
 */
export class ParentResizeManager {
  private messageHandler: ((event: MessageEvent<ResizeMessage>) => void) | null = null;
  private heightRequestInterval: number | null = null;
  private isDestroyed: boolean = false;

  constructor(
    private iframe: HTMLIFrameElement,
    private onResize: (dimensions: { height: number; width: number }) => void,
    private options: ResizeOptions = {}
  ) {
    this.options = {
      debounceMs: 16,
      ...options,
    };

    this.initialize();
  }

  private initialize(): void {
    if (!this.iframe?.contentWindow) {
      console.warn('Iframe or its contentWindow is not available');
      return;
    }

    this.setupMessageHandler();
    this.requestInitialHeight();
  }

  private setupMessageHandler(): void {
    this.messageHandler = (event: MessageEvent<ResizeMessage>) => {
      if (this.isDestroyed) return;

      try {
        // Only process messages from our iframe
        if (event.source !== this.iframe.contentWindow) {
          return;
        }

        const { data } = event;
        if (!data || data.type !== 'resize') {
          return;
        }

        // Validate message has required data
        if (typeof data.height !== 'number') {
          return;
        }

        // If elementId is provided, only process messages for this element
        if (this.options.elementId && data.elementId !== this.options.elementId) {
          return;
        }

        // Apply constraints on parent side as well
        let { height, width = 0 } = data;
        height = Math.max(height, this.options.minHeight || 0);
        height = Math.min(height, this.options.maxHeight || Infinity);

        this.onResize({ height, width });
      } catch (error) {
        console.error('Error handling resize message:', error);
      }
    };

    window.addEventListener('message', this.messageHandler);
  }

  private requestInitialHeight(): void {
    // Request height once iframe is loaded
    const requestHeight = () => {
      if (this.isDestroyed || !this.iframe?.contentWindow) return;

      try {
        const message: ResizeMessage = { type: 'getHeight' };
        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }
        this.iframe.contentWindow.postMessage(message, '*');
      } catch (error) {
        console.error('Error requesting height:', error);
      }
    };

    if (this.iframe.contentDocument?.readyState === 'complete') {
      // Small delay to ensure content is rendered
      setTimeout(requestHeight, 100);
    } else {
      this.iframe.addEventListener('load', () => {
        setTimeout(requestHeight, 100);
      }, { once: true });
    }
  }

  public updateConstraints(constraints: ResizeOptions): void {
    this.options = { ...this.options, ...constraints };

    // Send constraint update to iframe
    if (this.iframe?.contentWindow) {
      try {
        const message: ResizeMessage = {
          type: 'constraintsUpdate',
          constraints,
        };

        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }

        this.iframe.contentWindow.postMessage(message, '*');
      } catch (error) {
        console.error('Error sending constraint update:', error);
      }
    }

    // Automatically recalculate size to apply new constraints
    this.recalculateSize();
  }

  public recalculateSize(): void {
    if (this.isDestroyed || !this.iframe?.contentWindow) {
      console.warn('Cannot recalculate size: iframe not available or manager destroyed');
      return;
    }

    try {
      // First, request current content height
      const message: ResizeMessage = { type: 'getHeight' };
      if (this.options.elementId) {
        message.elementId = this.options.elementId;
      }
      this.iframe.contentWindow.postMessage(message, '*');

      // Also immediately apply current constraints to force resize
      // This handles cases where content is larger than maxHeight constraint
      if (this.options.maxHeight && this.options.maxHeight !== Infinity) {
        const constrainedHeight = this.options.maxHeight;
        this.onResize({ 
          height: constrainedHeight, 
          width: this.iframe.offsetWidth || 0 
        });
      }
    } catch (error) {
      console.error('Error requesting size recalculation:', error);
    }
  }

  public destroy(): void {
    this.isDestroyed = true;

    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    if (this.heightRequestInterval) {
      clearInterval(this.heightRequestInterval);
      this.heightRequestInterval = null;
    }
  }
}

/**
 * Setup function for iframe content (replaces setupIframeContentResize)
 */
export function setupUnifiedContentResize(options: ResizeOptions = {}): () => void {
  const manager = new IframeContentResizeManager(options);
  return () => manager.destroy();
}

/**
 * Setup function for parent window (replaces setupIframeResize)
 * Returns both cleanup function and manager instance for full functionality
 */
export function setupUnifiedParentResize(
  iframe: HTMLIFrameElement,
  onResize: (dimensions: { height: number; width: number }) => void,
  options: ResizeOptions = {}
): { cleanup: () => void; manager: ParentResizeManager } {
  const manager = new ParentResizeManager(iframe, onResize, options);
  return {
    cleanup: () => manager.destroy(),
    manager
  };
}
